import { useState, useEffect } from "preact/hooks";
import { authHeader } from "../auth.ts";
import type { AppState } from "../state/actions.ts";
import type { TelegramDocument } from "../types/document.ts";

interface User {
  id: number;
  username: string;
  displayName: string;
}

interface Props {
  currentUserId: number | null;
  state: AppState;
  onSent: () => void;
}

export function SendForm({ currentUserId, state, onSent }: Props) {
  const [users, setUsers] = useState<User[]>([]);
  const [recipientId, setRecipientId] = useState<number | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/users", { headers: authHeader() })
      .then((r) => r.json())
      .then(setUsers)
      .catch(() => setError("Failed to load users"));
  }, []);

  const send = async () => {
    if (!currentUserId || !recipientId) return;
    setSending(true);
    setError(null);

    const doc: TelegramDocument = {
      version: 1,
      sentAt: new Date().toISOString(),
      text: { html: state.textHtml, style: state.textStyle },
      canvas: { width: state.canvasWidth, height: state.canvasHeight },
      objects: state.objects,
    };

    const res = await fetch("/api/telegrams", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({
        recipientId,
        content: JSON.stringify(doc),
      }),
    });

    setSending(false);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      setError(body.error ?? "Send failed");
      return;
    }
    onSent();
  };

  const recipients = users.filter((u) => u.id !== currentUserId);

  return (
    <div class="flex items-center gap-3">
      <label class="text-sm" style={{ color: "#5D4E37" }}>
        To:
        <select
          class="ml-1 rounded px-2 py-1 text-sm"
          style={{ background: "#FFF8E7", border: "1px solid #D4B078", color: "#5D4E37" }}
          value={recipientId ?? ""}
          onChange={(e) => setRecipientId(Number((e.target as HTMLSelectElement).value) || null)}
        >
          <option value="">Select recipient...</option>
          {recipients.map((u) => (
            <option key={u.id} value={u.id}>
              {u.displayName}
            </option>
          ))}
        </select>
      </label>
      <button
        class="px-4 py-1 rounded-lg text-sm disabled:opacity-50"
        style={{ background: "#C48A3F", color: "#FFF8E7" }}
        disabled={!recipientId || sending}
        onClick={send}
      >
        {sending ? "Sending..." : "Send Telegram"}
      </button>
      {error && <span class="text-sm text-red-600">{error}</span>}
    </div>
  );
}
